import { router } from "@inertiajs/react";
import { ArrowLeft, ArrowRight, BookOpen, Lock, RotateCcw, SkipForward } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { PHASES, STEP_COPY, WAITING_ON_REASONS, LOCKED_LABEL, displayStatus, helpHref, listJoin } from "@/components/schools/setup/steps";
import { TierBadge, DueChip, EffortChip, StepNotice, StepStatusIcon } from "@/components/schools/setup/SetupPrimitives";

/**
 * The frame around one setup step: what it is and why it matters, any
 * step it's waiting on, the step's own content, and the controls to
 * skip it or move on.
 */
export default function StepPanel({ step, steps, today, skipPath, onSelect, children }) {
  const copy = STEP_COPY[step.key];
  const index = steps.indexOf(step);
  const phase = PHASES.find((p) => p.key === step.phase);
  const previous = steps[index - 1];
  const next = steps[index + 1];
  const locked = displayStatus(step) === "locked";
  const Icon = locked ? Lock : copy.icon;

  return (
    <Card className="overflow-hidden">
      <header className="border-b border-border p-6 md:p-8">
        <p className="mb-3 text-xs uppercase tracking-wider text-muted-foreground">
          Step {index + 1} of {steps.length} · {phase.title}
        </p>
        <div className="flex items-start gap-4">
          <span className={cn("hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl sm:inline-flex", locked ? "bg-warning/15 text-warning-foreground" : "bg-primary/10 text-primary")}>
            <Icon className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1 space-y-3">
            <h2 className="font-display text-2xl font-semibold leading-tight text-foreground md:text-3xl">{copy.title}</h2>
            <div className="flex flex-wrap items-center gap-2">
              <TierBadge tier={step.tier} />
              {locked && <Badge variant="warning">{LOCKED_LABEL}</Badge>}
              <DueChip step={step} today={today} />
              {step.status !== "done" && <EffortChip effort={copy.effort} />}
            </div>
            <p className="max-w-2xl leading-relaxed text-muted-foreground">{copy.why}</p>
          </div>
        </div>
      </header>

      <div className="space-y-6 p-6 md:p-8">
        {/* Launch explains its own blockers in the pre-flight check. */}
        {step.status === "blocked" && step.blocked_by.length > 0 && step.key !== "launch" && (
          <StepNotice
            tone="info"
            action={
              <Button size="sm" variant="outline" onClick={() => onSelect(step.blocked_by[0])}>
                Go to {STEP_COPY[step.blocked_by[0]].short}
              </Button>
            }
          >
            This step opens once you've finished {quoteSteps(step.blocked_by)}.
          </StepNotice>
        )}

        {step.status !== "done" &&
          step.waiting_on.map((key) => (
            <StepNotice
              key={key}
              tone="info"
              action={<Button size="sm" variant="outline" onClick={() => onSelect(key)}>Go to {STEP_COPY[key].short}</Button>}
            >
              {WAITING_ON_REASONS[step.key]?.[key]}
            </StepNotice>
          ))}

        {step.status === "skipped" && (
          <StepNotice
            tone="info"
            action={<SkipButton path={skipPath} skipped={false} />}
          >
            You skipped this step. It still counts toward your progress, and you can come back to it any time before launch.
          </StepNotice>
        )}

        {children}
      </div>

      <footer className="flex flex-col-reverse gap-3 border-t border-border bg-background/50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between md:px-8">
        <div className="flex flex-wrap items-center gap-2">
          {step.skippable && step.status !== "done" && step.status !== "skipped" && (
            <SkipButton path={skipPath} skipped />
          )}
          <Button variant="ghost" size="sm" asChild>
            <a href={helpHref(copy.help)} target="_blank" rel="noopener">
              <BookOpen className="h-4 w-4" />
              Help article
            </a>
          </Button>
        </div>
        <div className="flex items-center gap-2">
          {previous && (
            <Button variant="outline" size="sm" onClick={() => onSelect(previous.key)}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          )}
          {next && (
            <Button variant={step.status === "done" || step.status === "skipped" ? "default" : "outline"} size="sm" onClick={() => onSelect(next.key)}>
              <span><span className="hidden sm:inline">Next: </span>{STEP_COPY[next.key].short}</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </footer>
    </Card>
  );
}

// “Connect Square” and “Add your students”
export function quoteSteps(keys) {
  return listJoin(keys.map((key) => `“${STEP_COPY[key].title}”`));
}

function SkipButton({ path, skipped }) {
  const submit = () =>
    router.patch(path, { skipped }, { preserveScroll: true });

  return skipped ? (
    <Button variant="ghost" size="sm" onClick={submit}>
      <SkipForward className="h-4 w-4" />
      Skip this step
    </Button>
  ) : (
    <Button variant="outline" size="sm" onClick={submit}>
      <RotateCcw className="h-4 w-4" />
      Bring it back
    </Button>
  );
}

// A step's "you've done this" summary: a green check with what's set,
// and the ways to change it.
export function DoneSummary({ title, children, actions }) {
  return (
    <div className="rounded-xl border border-success/25 bg-success/5 p-5">
      <div className="flex items-start gap-3">
        <StepStatusIcon status="done" />
        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <p className="font-medium text-foreground">{title}</p>
            {children && <div className="mt-1 text-sm leading-relaxed text-muted-foreground">{children}</div>}
          </div>
          {actions}
        </div>
      </div>
    </div>
  );
}
